import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, ArrowUpRight } from "lucide-react";
import { PageHead } from "@/components/utils";
import { FadeUp } from "@/components/Reveal";
import { BUSINESS } from "@/lib/data";
import { IDS } from "@/lib/testIds";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.message) {
      toast.error("Please share your name and a short message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSubmitted(true);
      toast.success("Message received — we'll be in touch shortly.");
      setForm({ name: "", phone: "", email: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <PageHead
        title="Contact — Good Looks Unisex Salon, Mansa"
        description="Contact Good Looks Salon, Sunni Gali, Sadar, Mansa. Call, message, or find our location and opening hours."
      />
      <section className="bg-[#F3EAD9] pt-40 pb-16">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10">
          <FadeUp>
            <p className="eyebrow text-[#C89B3C] mb-6">— Contact</p>
            <h1 className="font-display text-5xl md:text-8xl leading-[0.95] max-w-5xl">
              Say hello.<br /><span className="italic">We'll say it back.</span>
            </h1>
          </FadeUp>
        </div>
      </section>

      <section className="bg-[#F3EAD9] pb-24">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5 space-y-10">
            <FadeUp>
              <div>
                <p className="eyebrow text-black/50 mb-3">Studio</p>
                <p className="font-display text-2xl leading-tight">{BUSINESS.name}</p>
                <p className="mt-2 text-black/70 text-sm leading-relaxed flex items-start gap-2">
                  <MapPin size={14} className="mt-1 text-[#C89B3C]" />
                  <span>{BUSINESS.address.line1}, {BUSINESS.address.line2}</span>
                </p>
              </div>
            </FadeUp>

            <FadeUp delay={0.05}>
              <div>
                <p className="eyebrow text-black/50 mb-3">Reach us</p>
                <a href={BUSINESS.phoneHref} className="block gl-link text-lg font-display">
                  <Phone size={14} className="inline mr-2 text-[#C89B3C]" />
                  {BUSINESS.phone}
                </a>
                <a href={`mailto:${BUSINESS.email}`} className="block gl-link text-lg font-display mt-2">
                  <Mail size={14} className="inline mr-2 text-[#C89B3C]" />
                  {BUSINESS.email}
                </a>
              </div>
            </FadeUp>

            <FadeUp delay={0.1}>
              <div>
                <p className="eyebrow text-black/50 mb-3">Hours</p>
                <div className="grid grid-cols-2 gap-y-1 text-sm text-black/75 max-w-md">
                  {BUSINESS.hours.map((h) => (
                    <div key={h.day} className="contents">
                      <span>{h.day}</span>
                      <span>{h.time}</span>
                    </div>
                  ))}
                </div>
              </div>
            </FadeUp>
          </div>

          <FadeUp className="lg:col-span-7" delay={0.1}>
            <form
              onSubmit={submit}
              data-testid={IDS.contactForm}
              className="border border-black/10 bg-white p-8 md:p-10 space-y-6"
            >
              <div className="flex items-baseline justify-between">
                <h2 className="font-display text-3xl">Send a message</h2>
                <span className="chapter-number">01</span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label className="eyebrow text-black/50">Name</Label>
                  <Input
                    data-testid="contact-name"
                    value={form.name}
                    onChange={update("name")}
                    required
                    className="mt-2 h-12 rounded-none border-x-0 border-t-0 border-b border-black/20 focus:border-[#C89B3C] px-0 bg-transparent"
                  />
                </div>
                <div>
                  <Label className="eyebrow text-black/50">Phone</Label>
                  <Input
                    data-testid="contact-phone"
                    value={form.phone}
                    onChange={update("phone")}
                    className="mt-2 h-12 rounded-none border-x-0 border-t-0 border-b border-black/20 focus:border-[#C89B3C] px-0 bg-transparent"
                  />
                </div>
              </div>

              <div>
                <Label className="eyebrow text-black/50">Email</Label>
                <Input
                  data-testid="contact-email"
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  className="mt-2 h-12 rounded-none border-x-0 border-t-0 border-b border-black/20 focus:border-[#C89B3C] px-0 bg-transparent"
                />
              </div>

              <div>
                <Label className="eyebrow text-black/50">Message</Label>
                <Textarea
                  data-testid="contact-message"
                  value={form.message}
                  onChange={update("message")}
                  required
                  rows={5}
                  className="mt-2 rounded-none border-x-0 border-t-0 border-b border-black/20 focus:border-[#C89B3C] px-0 bg-transparent resize-none"
                />
              </div>

              <div className="flex items-center justify-between pt-4">
                {submitted ? (
                  <p className="text-sm text-[#C89B3C]">Thank you — we'll be in touch.</p>
                ) : (
                  <p className="text-xs text-black/50">
                    We'll reply within one working day.
                  </p>
                )}
                <button
                  type="submit"
                  data-testid={IDS.contactSubmit}
                  disabled={loading}
                  className="btn-gold disabled:opacity-60"
                >
                  <span>{loading ? "Sending…" : "Send message"}</span>
                  <ArrowUpRight size={16} />
                </button>
              </div>
            </form>
          </FadeUp>
        </div>
      </section>

      <section className="bg-[#1B1B1B] py-16">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10">
          <div className="aspect-[16/8] w-full border border-white/10 overflow-hidden">
            <iframe
              title="Good Looks Salon location"
              src={BUSINESS.mapEmbed}
              width="100%"
              height="100%"
              style={{ border: 0, filter: "grayscale(0.6) invert(0.9)" }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </section>
    </>
  );
}
